import * as React from "react";
import { useTranslation } from "react-i18next";
import { ScrollArea } from "@radix-ui/react-scroll-area";
import { CheckIcon, ChevronsUpDown } from "lucide-react";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from "@/components/ui/sidebar";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";

export function ClientSwitcher() {
  const { isMobile } = useSidebar();
  const [open, setOpen] = React.useState(false);
  const { user, switchClientMutation } = useAuth();
  const { toast } = useToast();
  const { t } = useTranslation();

  if (!user?.clients || !user?.selected_client_id) {
    return null;
  }

  const activeClient = user.clients.find((client) => client.id === user.selected_client_id);

  const handleClientSwitch = async (clientCode: string) => {
    try {
      await switchClientMutation.mutateAsync(clientCode);
      window.location.reload();
      setOpen(false);
    } catch (error) {
      console.error("Failed to switch client:", error);
      toast({
        title: "Failed to switch client",
        description: "Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <div className="sidebar-primary text-sidebar-primary-foreground flex aspect-square size-8 items-center justify-center rounded-lg">
                <ChevronsUpDown className="size-4" />
              </div>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-semibold">{activeClient?.client_name}</span>
                <span className="truncate text-xs">{t("Switch Client")}</span>
              </div>
            </SidebarMenuButton>
          </PopoverTrigger>
          <PopoverContent
            className="w-[--radix-popover-trigger-width] min-w-56 rounded-lg p-0"
            align="start"
            side={isMobile ? "bottom" : "right"}
            sideOffset={4}
          >
            <Command>
              <CommandInput placeholder={t("Search client...")} className="h-9" />
              <ScrollArea className="max-h-96">
                <CommandEmpty>{t("No client found.")}</CommandEmpty>
                <CommandGroup heading={t("Selected Client")}>
                  <CommandItem
                    key={activeClient?.id}
                    value={activeClient?.client_code || ""}
                    className="gap-2 p-2"
                    disabled
                  >
                    <CheckIcon className="mr-2 h-4 w-4 opacity-100" />
                    {activeClient?.client_name}
                  </CommandItem>
                </CommandGroup>
                <CommandGroup heading={t("Available Clients")}>
                  <CommandList>
                    {user.clients
                      .filter((client) => client.id !== user.selected_client_id)
                      .map((client) => (
                        <CommandItem
                          key={client.id}
                          value={client.client_name}
                          onSelect={() => handleClientSwitch(client.client_code)}
                          disabled={switchClientMutation.isPending}
                          className="cursor-pointer gap-2 p-2"
                        >
                          <CheckIcon className={cn("mr-2 h-4 w-4 opacity-0")} />
                          {client.client_name}
                        </CommandItem>
                      ))}
                  </CommandList>
                </CommandGroup>
              </ScrollArea>
            </Command>
          </PopoverContent>
        </Popover>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
